import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Chip,
  Skeleton,
  Link
} from '@mui/material';
import {
  Add as AddIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { calcReputationDelta } from '../../utils/reputationUtils';
import ReputationPill from '../common/ReputationPill';
import { FancySkeleton } from '../common/FancySkeleton';
import { useSimulation } from '../../contexts/SimulationContext';
import { useCreateWithTrialCheck } from '../../hooks/useCreateWithTrialCheck';
import TrialStatusModal from '../TrialStatusModal';

interface RecentSimulation {
  id: string;
  content: string;
  createdAt: string;
  platform: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  reputationDelta?: number;
  reputationUIScore?: number;
  reputationLevel?: string;
  reputationColor?: string;
}

interface RecentSimulationsProps {
  simulations?: RecentSimulation[];
  loading?: boolean;
}


export const RecentSimulations: React.FC<RecentSimulationsProps> = ({
  simulations = [],
  loading = false
}) => {
  const navigate = useNavigate();
  const { dispatch } = useSimulation();
  
  const {
    handleCreateAction,
    isTrialStatusModalOpen,
    closeTrialStatusModal,
    isAdmin
  } = useCreateWithTrialCheck();

  if (loading) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Simulations
          </Typography>
          <List>
            {[1, 2, 3].map((i) => (
              <ListItem key={i} sx={{ px: 0 }}>
                <ListItemText
                  primary={<Skeleton variant="text" width="80%" />}
                  secondary={<Skeleton variant="text" width="60%" />}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Recent Simulations
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/simulations')}
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            View All
          </Link>
        </Box>

        {simulations.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No simulations yet
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleCreateAction(() => {
                dispatch({ type: 'RESET_FORM' });
                navigate('/simulations/create');
              })}
              size="small"
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Create Your First Simulation
            </Button>
          </Box>
        ) : (
          <>
            <List sx={{ py: 0 }}>
              {simulations.map((sim) => (
                <ListItem
                  key={sim.id}
                  sx={{
                    px: 0,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                  onClick={() => navigate(`/simulations/${sim.id}/results`)}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {sim.content}
                      </Typography>
                      {(() => {
                        if (typeof sim.reputationUIScore === 'number' && sim.reputationLevel) {
                          return <ReputationPill uiScore={sim.reputationUIScore} level={sim.reputationLevel} />;
                        }
                        // In-progress: show skeleton instead of neutral default
                        return <FancySkeleton height={18} width={110} radius={10} />;
                      })()}
                    </Box>
                    <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {sim.platform}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(sim.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleCreateAction(() => {
                dispatch({ type: 'RESET_FORM' });
                navigate('/simulations/create');
              })}
              fullWidth
              size="small"
              sx={{ mt: 2, textTransform: 'none' }}
            >
              New Simulation
            </Button>
          </>
        )}
      </CardContent>
    </Card>
    
    <TrialStatusModal 
      isOpen={isTrialStatusModalOpen} 
      onClose={closeTrialStatusModal} 
      isAdmin={isAdmin}
      status="expired"
    />
    </>
  );
};
